import { useCallback, useState, useEffect } from 'react'
import { IAutocompleteOption } from 'transfix-ui/components/Autocomplete'
import { INewCustomer, createNewCustomerRequest } from 'apps/fleet-planner/schema/CustomerSchema'
import * as Types from 'apps/fleet-planner/types'
import * as Api from 'apps/fleet-planner/utils/api'

interface IUseCustomerApi {
  isLoading: boolean
  customers: Types.ICustomer[]
  error?: string
  getAndSetCustomers: () => Promise<void>
  getCustomerOptions: (groupOptions: { groupIndex: number }) => IAutocompleteOption[]
  getCustomerById: (customerId: string) => Types.ICustomer | undefined
  createNewCustomer: (customer: INewCustomer) => Promise<Types.ICustomer | undefined>
  updateCustomer: (
    customerId: string,
    customer: Partial<Types.ICustomer>
  ) => Promise<Types.ICustomer | undefined>
}

/**
 * Hook for fetching/storing customer query data and state
 * @param {string} [searchQuery] - Optional string used to filter customer query
 * @see https://fp-staging.transfix.io/api-docs/index.html
 */
export const useCustomerApi = (
  searchQuery?: string,
  fetchList: boolean = true
): IUseCustomerApi => {
  const [isLoading, setLoading] = useState(false)
  const [error, setError] = useState<string | undefined>()
  const [customers, setCustomers] = useState<Types.ICustomer[]>([])

  /**
   * Update customers in local state
   * @param {string} [name] - optional `name` filter value
   */
  const getAndSetCustomers = useCallback(async (name?: string) => {
    setLoading(true)
    try {
      const { customers: customerList } = await Api.fetchCustomers({
        filters: {
          name,
        },
      })
      setCustomers(customerList)
      setLoading(false)
    } catch (e) {
      setLoading(false)
      setError(e)
    }
  }, [])

  /**
   * Find a customer from local state
   * @param {string} customerId
   */
  const getCustomerById = useCallback(
    (customerId: string) => {
      return customers.find(({ id }) => customerId === id)
    },
    [customers]
  )

  /**
   * Get list of Autocomplete-compatible options with
   * optional and dynamic `groupIndex` param
   */
  const getCustomerOptions = useCallback(
    (groupOptions: { groupIndex: number }): IAutocompleteOption[] => {
      return customers.map(({ id = '', name: text = '' }) => ({
        id,
        text,
        ...groupOptions,
      }))
    },
    [customers]
  )

  /**
   * Create new customer entity
   */
  const createNewCustomer = useCallback(async (c: INewCustomer): Promise<
    Types.ICustomer | undefined
  > => {
    setLoading(true)
    try {
      const req = createNewCustomerRequest(c)
      const { customer } = await Api.createNewCustomer(req)
      setLoading(false)
      setError(undefined)
      return customer
    } catch (e) {
      setError(e)
      setLoading(false)
      return undefined
    }
  }, [])

  /**
   * Update existing customer entity
   */
  const updateCustomer = useCallback(
    async (
      customerId: string,
      changes: Partial<Types.ICustomer>
    ): Promise<Types.ICustomer | undefined> => {
      setLoading(true)
      try {
        const { customer } = await Api.updateCustomer(customerId, changes)
        setLoading(false)
        setError(undefined)
        return customer
      } catch (e) {
        setError(e)
        setLoading(false)
        return undefined
      }
    },
    []
  )

  /**
   * Fetch all customers on mount,
   * and update when `searchQuery` changes
   */
  useEffect(() => {
    async function fetchOnMount() {
      await getAndSetCustomers(searchQuery)
    }
    if (fetchList) fetchOnMount()
  }, [fetchList, getAndSetCustomers, searchQuery])

  return {
    isLoading,
    customers,
    error,
    createNewCustomer,
    updateCustomer,
    getAndSetCustomers,
    getCustomerOptions,
    getCustomerById,
  }
}
